import { Component, OnInit, OnDestroy } from '@angular/core';
import { Connection as ApideckConnection } from '@apideck/vault-js';
import { StatementsAccountingIntegrationService } from '@app/modules/statements/services/statements-accounting-integration.service';
import { StatementsFacade } from '@app/modules/statements/services/statements.facade';
import { PusherService } from '@app/shared/services/pusher/pusher.service';
import { map, takeUntil, filter } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Connection } from '@app/modules/statements/models/statements.models';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { DialogAccountingSetupComponent } from '../../components/dialog-accounting-setup/dialog-accounting-setup.component';

enum VaultState {
  Callable = 'callable',
  Available = 'available',
  Added = 'added',
  Invalid = 'invalid',
  Authorized = 'authorized',
}

const CONNECTION_STATUS_LABEL_MAP = {
  [VaultState.Callable]: 'Connected',
  [VaultState.Available]: 'Available',
  [VaultState.Added]: 'Added',
  [VaultState.Invalid]: 'Invalid',
  [VaultState.Authorized]: 'Authorized',
};

@Component({
  standalone: false,
  selector: 'sbnb-statements-accounting-connections',
  templateUrl: './statements-accounting-connections.component.html',
})
export class StatementsAccountingConnectionsComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();
  public connectionsState$ = this.statementsFacade.connectionsState$;
  public displayedColumns = ['connection', 'status', 'actions'];
  public connecting$ = this.accountingIntegrationService.connecting$;
  public connectionStatusLabelMap = CONNECTION_STATUS_LABEL_MAP;
  public loadingVault$ = this.accountingIntegrationService.loadingVault$;
  public debugging$ = this.route.queryParams.pipe(map((params) => params['debug']));
  public deleting$ = this.accountingIntegrationService.deletingConnection$;

  constructor(
    private statementsFacade: StatementsFacade,
    private accountingIntegrationService: StatementsAccountingIntegrationService,
    private pusher: PusherService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastNotificationsService,
    private dialog: MatDialog
  ) {
    this.statementsFacade.listConnections();
  }

  ngOnInit(): void {
    this.listenForPusherEvents();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  openVault(): void {
    this.dialog
      .open(DialogAccountingSetupComponent)
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.accountingIntegrationService.openVault();
      });
  }

  openVaultForConnection(connection: ApideckConnection) {
    this.accountingIntegrationService.openVaultForConnection(connection);
  }

  deleteConnectionPrompt(connection: Connection): void {
    this.accountingIntegrationService.deleteConnectionPrompt(connection);
  }

  private listenForPusherEvents() {
    this.pusher.accountingConnectionUpdate$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        (data: {
          type: 'failed_onboarding' | 'finished_onboarding' | 'ledger_accounts_synced';
          connection: ApideckConnection;
        }) => {
          if (data.type === 'failed_onboarding') {
            this.toast.error('Failed to connect to Quickbooks');
            return;
          }

          if (data.type === 'finished_onboarding') {
            this.toast.success('Connected to Quickbooks');
            return;
          }

          if (data.connection.state === 'callable' && data.connection.id) {
            this.accountingIntegrationService.connected();
            this.router.navigate(['./', data.connection.id, 'new'], { relativeTo: this.route });
          }
        }
      );
  }
}
